import { Box, Paper, ScrollArea, Table } from '@mantine/core';
import { Fragment, useMemo } from 'react';
import { SurveyModel } from 'survey-core';
import CustomToggleButton from './RubricToggle';

interface Pos {
  start?: number;
  end?: number;
  pos?: Pos;
}

interface RubricCell {
  name: string;
  value?: string;
  text?: string;
  score?: number;
  enableIf?: string;
  pos?: Pos;
  [key: string]: unknown;
}

export interface MappedRubricRow {
  id: string;
  cells: RubricCell[];
}

interface RubricColumn {
  field: string;
  headerName?: string;
  header?: string;
  width?: number | string;
}

const FunctionalQuestionRubricGrid = (props: {
  columns: Array<RubricColumn>;
  rows: MappedRubricRow[];
  headerAlign: 'left' | 'center' | 'right';
  headerVerticalAlign: 'top' | 'middle' | 'bottom';
  cellAlign: 'left' | 'center' | 'right';
  cellVerticalAlign: 'top' | 'middle' | 'bottom';
  bandedRows: boolean;
  readOnly: boolean;
  survey: SurveyModel;
  allowMultipleValuesInRow: boolean;
  questionName: string;
  value: unknown;
  setValue: (val: unknown) => void;
}) => {
  const {
    columns,
    rows,
    headerAlign,
    headerVerticalAlign,
    cellAlign,
    cellVerticalAlign,
    bandedRows,
    readOnly,
    survey
  } = props;

  const cleanedCols = useMemo(() => {
    return JSON.parse(JSON.stringify(columns));
  }, [columns]);
  const cleanedRows = useMemo(() => {
    return JSON.parse(JSON.stringify(rows)) as typeof rows;
  }, [rows]);

  return (
    <Paper>
      <ScrollArea>
        <Table
          striped={bandedRows}
          style={{
            whiteSpace: 'normal',
            tableLayout: 'fixed',
            height: 'fit-content'
          }}
        >
          <Table.Thead>
            <Table.Tr>
              {cleanedCols?.length > 0 &&
                columns.map((col) => (
                  <Table.Th
                    key={col.field}
                    dangerouslySetInnerHTML={{
                      __html: col.header || col.headerName || ''
                    }}
                    style={{
                      textAlign: headerAlign,
                      verticalAlign: headerVerticalAlign,
                      fontSize: '1.4rem',
                      ...(col.width && { width: col.width }),
                      height: '100%'
                    }}
                  />
                ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {cleanedRows?.length > 0 &&
              cleanedRows.map((row) => {
                const rowId = row.id;

                return (
                  <Fragment key={rowId as string}>
                    <Table.Tr style={{ height: '100%' }}>
                      {columns.map(({ field, ...col }) => {
                        const cell = row.cells.find((r) => r.name === field);
                        // Check if toggle is enabled
                        const isEnabled = () => {
                          if (readOnly) {
                            return false;
                          }

                          if (cell?.enableIf) {
                            return survey.runExpression(cell.enableIf);
                          }
                          return true;
                        };

                        const displayValue =
                          cell?.text ||
                          (typeof cell === 'string' || typeof cell === 'number' ? cell : null);

                        return (
                          <Table.Td
                            key={cell?.name + field}
                            style={{
                              textAlign: cellAlign,
                              verticalAlign: cellVerticalAlign,
                              fontSize: '1.4rem',
                              ...(col.width && { width: col.width }),
                              padding: 0,
                              height: '100%'
                            }}
                          >
                            <Box style={{ position: 'relative', height: '100%' }}>
                              {field !== 'id' ? (
                                cell?.value ? (
                                  <CustomToggleButton
                                    row={row}
                                    id={rowId as string}
                                    disabled={!isEnabled()}
                                    displayValue={displayValue}
                                    fieldValue={cell?.value}
                                    field={field}
                                    {...props}
                                  />
                                ) : (
                                  <></>
                                )
                              ) : (
                                <Box style={{ padding: 16 }}>{displayValue}</Box>
                              )}
                            </Box>
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  </Fragment>
                );
              })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
};

export default FunctionalQuestionRubricGrid;
